'use client';

import React, { useCallback, useState } from 'react';
import { z } from 'zod';
import classNames from 'classnames';
import { merge } from 'lodash';
import type { ContentComponent } from '$cms/ContentComponent';
import type { SchemaType } from '$util/schema';
import { CmsShortTextSchema, CmsLinkSchema } from '$cms/types';
import { makeContentComponentDefaultConfig } from '$util/makeContentComponentDefaultConfig';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import { useModalQueue } from '$util/hooks/useModalQueue';
import { Link } from '$quantum/atoms/Link';
import { Card } from '@/components/atoms/Card';
import { Icon } from '@/components/atoms/Icon';
import { Img } from '@/components/atoms/Img';
import { SpeechBubble } from '@/components/atoms/SpeechBubble';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { Col, Row } from '@/components/atoms/Grid';

import styles from './index.module.scss';

const ItemIndexSchema = z.union([
  z.literal('1'),
  z.literal('2'),
  z.literal('3'),
  z.literal('4'),
  z.literal('5'),
]);
type ItemIndex = z.infer<typeof ItemIndexSchema>;

const ConfigColorSchema = z.union([z.literal('purple'), z.literal('green')]);
type ConfigColor = z.infer<typeof ConfigColorSchema>;

const defaultConfiguration = makeContentComponentDefaultConfig({
  colour: 'purple' as ConfigColor,
});

const BrandedIconListCardSchema = {
  translatedEntities: z.object({
    itemIcons: z.record(ItemIndexSchema, CmsShortTextSchema),
    itemText: z.record(ItemIndexSchema, CmsShortTextSchema),
    itemSentence: z.record(ItemIndexSchema, CmsShortTextSchema).optional(),
    speechBubbleText: CmsShortTextSchema.optional(),
    modalSpeechBubbleText: CmsShortTextSchema.optional(),
    link: CmsLinkSchema.optional(),
  }),
  configuration: z
    .object({
      lazyLoadImages: z.boolean().optional(),
      preloadImages: z.boolean().optional(),
      colour: ConfigColorSchema.optional(),
    })
    .default(defaultConfiguration),
};

export type BrandedIconListCardTranslatedEntities = SchemaType<
  (typeof BrandedIconListCardSchema)['translatedEntities']
>;
export type BrandedIconListCardConfig = SchemaType<(typeof BrandedIconListCardSchema)['configuration']>;

type ItemData = {
  readonly icon: string;
  readonly text: string;
  readonly sentence?: string;
};

type MoreDetailsModalProps = {
  items: ItemData[];
  readonly speechBubble?: string;
};

function MoreDetailsModal({ items, speechBubble }: MoreDetailsModalProps) {
  return (
    <div className={styles.modal}>
      {speechBubble && <SpeechBubble className={styles.modalSpeechBubble}>{speechBubble}</SpeechBubble>}
      <Img
        className={styles.fixieModalImage}
        provider="cloudinary"
        src="https://res.cloudinary.com/fxtr/image/upload/v1712854107/landing/fixie/fixie_waving_2_yczipr.png"
        alt="Image of Fixie tipping a chauffeur's hat and winking"
        width={122}
        height={117}
      />
      {items.map((item) => {
        const { icon, text, sentence } = item;

        if (icon && text) {
          return (
            <Card key={icon} className={classNames(styles.modalCard)} cornerRadius={3} elevation={2}>
              <div className={classNames(styles.item, styles.modalItem)}>
                <p className={styles.text}>{text}</p>
                {sentence && <p className={styles.sentenceText}>{sentence}</p>}
                <div className={classNames(styles.icon, styles.modalIcon)}>
                  <Icon legacy={false} id={icon} />
                </div>
              </div>
            </Card>
          );
        }
        return false;
      })}
    </div>
  );
}

export const BrandedIconListCard: ContentComponent<
  BrandedIconListCardTranslatedEntities,
  BrandedIconListCardConfig
> = function BrandedIconListCard({ translatedEntities, configuration, className, ...props }): JSX.Element {
  const [largeViewport, setLargeViewport] = useState(false);

  const isLargeViewport = useCallback((isLarge: boolean) => {
    setLargeViewport(isLarge);
  }, []);

  useResizeViewport({
    breakpoint: 'medium',
    direction: 'up',
    fn: isLargeViewport,
  });

  const [, queueModal] = useModalQueue();

  const { itemIcons, itemText, itemSentence, link, speechBubbleText, modalSpeechBubbleText } =
    translatedEntities;
  const { colour } = merge(defaultConfiguration, configuration);

  const speechText = speechBubbleText?.value;

  // get number of items to output
  const numberOfIcons: number = Object.keys(itemIcons).length;
  const numberOfTextItems: number = Object.keys(itemText).length;

  const numberToRender = Math.min(numberOfIcons, numberOfTextItems);

  const iconIds = Array.from({ length: numberToRender }, (_, i) => (i + 1).toString())
    .map((key) => itemIcons[key as ItemIndex]?.value || '')
    .filter((id) => !!id);
  const textContents = Array.from({ length: numberToRender }, (_, i) => (i + 1).toString())
    .map((key) => itemText[key as ItemIndex]?.value || '')
    .filter((text) => !!text);
  const sentenceContents = Array.from({ length: numberToRender }, (_, i) => (i + 1).toString()).map(
    (key) => itemSentence?.[key as ItemIndex]?.value || ''
  );

  const itemDataList = Array.from({ length: numberToRender }, (_, i) => `${i + 1}`).map((_, i) => ({
    icon: iconIds[i],
    text: textContents[i],
    sentence: sentenceContents[i],
  }));

  const openMoreDetailsModal = () => {
    queueModal(
      <MoreDetailsModal items={itemDataList} speechBubble={modalSpeechBubbleText?.value} />
    ).trackModalOpen('usp-hero-24', 'learn more link');
  };

  return (
    <ContentComponentSection
      className={classNames(
        BrandedIconListCard.displayName,
        styles.component,
        colour && colour === 'green' && styles.green,
        className
      )}
      {...props}
    >
      <Row>
        <Col className={styles.contentsColumn}>
          {speechText && (
            <SpeechBubble className={styles.speechBubble} tailSide={largeViewport ? 'left' : 'right'}>
              {speechText}
            </SpeechBubble>
          )}
          <Img
            className={styles.fixieImage}
            provider="cloudinary"
            src="https://res.cloudinary.com/fxtr/image/upload/v1712587511/landing/fixie/fixie-valet_f6btdu.png"
            alt="Image of Fixie tipping a chauffeur's hat and winking"
            width={191}
            height={128}
          />
          <Card className={styles.card} cornerRadius={3} elevation={2}>
            <ul className={styles.mainContent}>
              {itemDataList.map(({ icon, text }) => (
                <li key={icon} className={styles.item}>
                  <p className={styles.text}>{text}</p>
                  <div className={classNames(styles.icon, styles.adjust)}>
                    <Icon legacy={false} id={icon} />
                  </div>
                </li>
              ))}
            </ul>
            {link?.text && (
              <div className={styles.linkContainer}>
                <Link
                  className={styles.link}
                  direction="none"
                  type="S"
                  href={link.url}
                  onClick={(event) => {
                    event.preventDefault();
                    openMoreDetailsModal();
                  }}
                >
                  {link.text}
                </Link>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </ContentComponentSection>
  );
};

BrandedIconListCard.displayName = 'BrandedIconListCard';
BrandedIconListCard.schema = BrandedIconListCardSchema;
